import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt, BarChart } from './_finHelpers';

// IRA Calculator — compare Traditional/SEP/SIMPLE vs Roth vs Taxable
export default function IRACalc() {
  const [balance, setBalance] = useState(30000);
  const [contrib, setContrib] = useState(7500);
  const [rate, setRate] = useState(6);
  const [age, setAge] = useState(30);
  const [retAge, setRetAge] = useState(65);
  const [marginal, setMarginal] = useState(25);
  const [retTax, setRetTax] = useState(15);
  const c = useMemo(() => {
    const years = Math.max(0, retAge - age);
    const r = rate / 100;
    const t = marginal / 100;
    const tR = retTax / 100;
    // Traditional / SEP / SIMPLE: pre-tax contributions, taxed at retirement withdrawal.
    let trad = balance;                     // pre-tax balance
    // Roth: contribution is after-tax; both current balance and each contribution are converted
    let roth = balance * (1 - t);
    // Regular taxable savings: after-tax contributions and after-tax growth
    let tax = balance * (1 - t);
    const rows = [];
    for (let y = 1; y <= years; y++) {
      // Traditional grows tax-deferred at full rate; contribution added pre-tax
      trad = trad * (1 + r) + contrib;
      // Roth: contribution reduced by marginal tax; growth is tax-free
      roth = roth * (1 + r) + contrib * (1 - t);
      // Taxable: growth taxed each year; contribution after-tax
      tax  = tax  * (1 + r * (1 - t)) + contrib * (1 - t);
      rows.push({ age: age + y, trad, roth, tax });
    }
    const tradAfterTax = trad * (1 - tR);
    const totalPrincipal = contrib * years;
    return { trad, roth, tax, tradAfterTax, rows, totalPrincipal };
  }, [balance, contrib, rate, age, retAge, marginal, retTax]);
  return (
    <CalcShell look="cnet" title="IRA Calculator" subtitle="Compare Traditional / SEP / SIMPLE IRA vs. Roth IRA vs. Regular Taxable savings."
      about={<><p>Traditional IRAs use pre-tax contributions and are taxed on withdrawal. Roth IRAs use post-tax contributions and are tax-free on withdrawal. This calculator compares long-term outcomes.</p></>}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Current balance</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={balance} onChange={e=>setBalance(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Annual before tax contribution</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={contrib} onChange={e=>setContrib(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Expected rate of return</label><div className="flex gap-2"><input type="number" step="0.01" value={rate} onChange={e=>setRate(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <div className="field"><label>Current age</label><input type="number" value={age} onChange={e=>setAge(+e.target.value||0)}/></div>
          <div className="field"><label>Retirement age</label><input type="number" value={retAge} onChange={e=>setRetAge(+e.target.value||0)}/></div>
          <div className="field"><label>Current marginal tax rate</label><div className="flex gap-2"><input type="number" step="0.01" value={marginal} onChange={e=>setMarginal(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <div className="field"><label>Expected tax rate in retirement</label><div className="flex gap-2"><input type="number" step="0.01" value={retTax} onChange={e=>setRetTax(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
        </div>
        <div>
          <table className="data-table">
            <thead><tr><th className="text-left">At age {retAge}</th><th className="text-right">Traditional / SEP / SIMPLE</th><th className="text-right">Roth</th><th className="text-right">Taxable</th></tr></thead>
            <tbody>
              <tr><td>Balance</td><td className="text-right">{fmt(c.trad)}</td><td className="text-right">{fmt(c.roth)}</td><td className="text-right">{fmt(c.tax)}</td></tr>
              <tr><td>Balance (after tax)</td><td className="text-right font-semibold text-blue-700" data-testid="ira-trad">{fmt(c.tradAfterTax)}</td><td className="text-right font-semibold text-green-700" data-testid="ira-roth">{fmt(c.roth)}</td><td className="text-right font-semibold" data-testid="ira-taxable">{fmt(c.tax)}</td></tr>
            </tbody>
          </table>
          <div className="mt-4 p-4 bg-blue-50 rounded-lg text-sm text-slate-700">
            Total principal contributed: <b>{fmt(c.totalPrincipal + balance)}</b>
          </div>
        </div>
      </div>
      <div className="calc-card p-5 mt-6">
        <h3 className="font-bold text-slate-900 mb-3">Balance Growth Comparison</h3>
        <BarChart
          data={c.rows.filter((_,i)=> i%Math.max(1,Math.floor(c.rows.length/12))===0 || i===c.rows.length-1).map(r => ({
            label: String(r.age),
            values: [
              { value: r.trad, color: '#2563eb' },
              { value: r.roth, color: '#10b981' },
              { value: r.tax,  color: '#f97316' },
            ]
          }))}
          stack={false}
          legend={[
            { label: 'Traditional / SEP / SIMPLE', color: '#2563eb' },
            { label: 'Roth', color: '#10b981' },
            { label: 'Taxable', color: '#f97316' },
          ]}
        />
      </div>

      <div className="calc-card p-5 mt-6">
        <h3 className="font-bold text-slate-900 mb-3">Annual Schedule</h3>
        <div className="max-h-96 overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-blue-800 text-white sticky top-0"><tr><th className="px-3 py-2 text-left">Age</th><th className="px-3 py-2 text-right">Traditional</th><th className="px-3 py-2 text-right">Roth</th><th className="px-3 py-2 text-right">Taxable</th></tr></thead>
            <tbody>
              {c.rows.map((r,i)=>(<tr key={i} className={`border-b ${i%2?'bg-slate-50':''}`}><td className="px-3 py-2">{r.age}</td><td className="px-3 py-2 text-right">{fmt(r.trad)}</td><td className="px-3 py-2 text-right">{fmt(r.roth)}</td><td className="px-3 py-2 text-right">{fmt(r.tax)}</td></tr>))}
            </tbody>
          </table>
        </div>
      </div>
    </CalcShell>
  );
}
